import { useEffect, useState } from 'react';

import { api } from './api';

/**
 * Fetch an image that requires a signed-in session.
 *
 * A browser will not attach an Authorization header to `<img src>` — it issues a plain,
 * anonymous GET. That is why avatars and signatures never appeared: the file was served from a
 * route behind `requireAuth`, so every image request came back 401 and the element rendered
 * nothing. The fix is not to remove the authorization; a barrister's signature image is exactly
 * the thing that must stay behind it. The image is fetched through the same axios client as
 * every other call, and the bytes are handed to the element as an object URL.
 *
 * Results are cached per URL for the life of the page, because a list of colleagues renders the
 * same handful of avatars many times over and each one would otherwise be a separate request.
 */

/** url → the object URL of the already-fetched bytes. */
const CACHE = new Map<string, string>();
/** url → the in-flight request, so ten simultaneous renders make one call. */
const INFLIGHT = new Map<string, Promise<string | null>>();

/** Ours to fetch, or somebody else's to link to? */
function isOwnApiUrl(src: string): boolean {
  if (src.startsWith('/api/')) return true;
  try {
    return new URL(src, window.location.origin).pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

/** The path axios should request, relative to its `/api/v1` base. */
function toApiPath(src: string): string {
  const path = src.startsWith('/') ? src : new URL(src).pathname;
  return path.replace(/^\/api\/v1/, '');
}

async function load(src: string): Promise<string | null> {
  const cached = CACHE.get(src);
  if (cached) return cached;

  const existing = INFLIGHT.get(src);
  if (existing) return existing;

  const request = (async () => {
    try {
      const res = await api.get(toApiPath(src), { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(res.data as Blob);
      CACHE.set(src, objectUrl);
      return objectUrl;
    } catch {
      // A missing or forbidden image is not an error worth surfacing — the caller falls back to
      // initials. Anything louder would put a broken-image icon on every page of the product.
      return null;
    } finally {
      INFLIGHT.delete(src);
    }
  })();

  INFLIGHT.set(src, request);
  return request;
}

/**
 * Resolve an image URL to something an `<img>` can actually display.
 *
 * Returns the URL unchanged when it is not ours — an external gravatar or a data: URI needs no
 * session — and null while a protected image is still loading.
 */
export function useAuthenticatedImage(
  src: string | null | undefined,
  /**
   * Bump to force a refetch of the SAME url.
   *
   * Replacing a photo does not change its address — the serving URL is derived from the user id —
   * so without this the person uploads a new picture and goes on seeing the old one until a full
   * reload, which reads exactly like a failed upload.
   */
  reloadToken?: unknown,
): string | null {
  const needsAuth = !!src && isOwnApiUrl(src);
  const [resolved, setResolved] = useState<string | null>(() =>
    !src ? null : needsAuth ? (CACHE.get(src) ?? null) : src,
  );

  useEffect(() => {
    if (!src) { setResolved(null); return; }
    if (!needsAuth) { setResolved(src); return; }

    const cached = CACHE.get(src);
    if (cached) { setResolved(cached); return; }

    let live = true;
    void load(src).then((url) => { if (live) setResolved(url); });
    return () => { live = false; };
  }, [src, needsAuth, reloadToken]);

  return resolved;
}

/**
 * Forget a cached image.
 *
 * Called after an upload or a removal: the serving URL does not change when someone replaces
 * their photo, so without this the page would keep showing the old one until a full reload.
 */
export function forgetAuthenticatedImage(src?: string | null): void {
  if (src) {
    const url = CACHE.get(src);
    if (url) URL.revokeObjectURL(url);
    CACHE.delete(src);
    return;
  }
  for (const url of CACHE.values()) URL.revokeObjectURL(url);
  CACHE.clear();
}
